import {
  type DropdownOption,
  ChakraBox,
  ChakraFlex,
  FAIconName,
  SkeletonLoader,
} from '@equip.health/ui';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CalendarResource,
  CalendarResourceMapper,
  RecurringAppointmentUpdateOption,
  UNKNOWN_AVAILABILITY_RULE_ERROR,
} from '~/lib/constants/calendar.constants';
import useCalendarElement from '~/lib/hooks/useCalendarElement';
import { toISO } from '~/lib/utils';
import TimezoneMenuV2 from '../TimezoneMenuV2';
import { AppointmentAttendeesAllowed } from '~/lib/constants/scheduling.constants';

import {
  generateAvailabilityQuery,
  Recurrence,
} from '~/lib/util/calendar.util';
import { ParticipantTypes } from '~/lib/constants/schedule.constants';
import {
  getRecurrenceOptions,
  isMonthlyAllowedAppointmentTypes,
} from '../util';
import { useGetCronofyEventUidsQuery } from '~/store/services/appointment';
import AvailabilitySettings from '~/components/common/calendar/shared/AvailabilitySettings';
import ParticipantsList from '~/components/common/calendar/shared/ParticipantsList';
import { convertToLocalTime } from '~/lib/util/timezone.util';
import {
  type ScheduledAppointments,
  EditSteps,
  getCaption,
  getIntervalInMinutesByAppointmentType,
  getParticipatingProviderCalendarInfo,
  getQueryPeriodsForEditAppointment,
} from './util';
import type { Attendees } from '~/lib/types/appointment/Attendees';

export interface EditAppointmentSlotSelectionStepProps {
  attendees: Attendees;
  appointmentStartDateTime: string;
  appointmentTypeOptions: DropdownOption[];
  scheduledAppointments: ScheduledAppointments;
  onProviderAdded: (participants: Participant[]) => void;
  handleParticipantSelect: (
    participantId: string,
    userType: ParticipantTypes,
  ) => void;
  id: string;
  ignoreAvailabilitySettings: boolean;
  recurrenceOption: DropdownOption;
  editAppointment: ScheduleItemBase;
  selectedAppointmentType: AppointmentType;
  step: EditSteps;
  onAppointmentSlotSelect: (date: string) => void;
  onTimeZoneChange?: (tzid: string) => void;
  eventTitle: string;
  setIgnoreAvailabilitySettings: (status: boolean) => void;
  handleAppointmentTypeChange: (appointmentId: string) => void;
  shouldEditAllAppointments: RecurringAppointmentUpdateOption;
  shouldAllProvidersAvailable: boolean;
  onFrequencyChange: (frequency: DropdownOption) => void;
  setAvailabilityQuery: (
    query: CalendarResourceConfigAvailabilityQuery,
  ) => void;
  setElementToken: (token: string) => void;
  setHostAttendee: (participant: Participant) => void;
}

const EditAppointmentSlotSelectionStep = ({
  attendees,
  appointmentStartDateTime,
  appointmentTypeOptions,
  eventTitle,
  id: TOP_LEVEL_ID,
  ignoreAvailabilitySettings,
  onAppointmentSlotSelect,
  recurrenceOption,
  editAppointment,
  selectedAppointmentType,
  scheduledAppointments,
  setIgnoreAvailabilitySettings,
  step,
  handleAppointmentTypeChange,
  handleParticipantSelect,
  onProviderAdded,
  onTimeZoneChange,
  setHostAttendee,
  shouldEditAllAppointments,
  shouldAllProvidersAvailable,
  onFrequencyChange,
  setAvailabilityQuery,
  setElementToken,
}: EditAppointmentSlotSelectionStepProps) => {
  const {
    elementToken,
    generateElementToken,
    options,
    setOptions,
    setHostManagedAvailability,
  } = useCalendarElement(
    CalendarResource.RESCHEDULE_APPOINTMENT_DATE_TIME_PICKER,
  );

  const [
    disableIgnoreAvailabilitySettings,
    setDisableIgnoreAvailabilitySettings,
  ] = useState<boolean>(false);

  const { data: cronofyEventUids } = useGetCronofyEventUidsQuery(
    {
      appointmentExternalId: editAppointment.externalId,
      includeAllRecurrences:
        shouldEditAllAppointments === RecurringAppointmentUpdateOption.ALL,
    },
    { skip: !editAppointment.externalId },
  );

  const { title } = editAppointment;

  const {
    hostAttendee,
    patientAttendee,
    providerAttendees,
    supportAttendees,
    externalAttendees,
  } = attendees;

  const isPendingAvailabilityQuery = false;

  const [height, setHeight] = useState('0px');

  const activeAppointmentTypeMemo = useMemo(
    () => ({
      label: eventTitle,
      value: title,
    }),
    [eventTitle, title],
  );

  const [activeAppointmentType, setActiveAppointmentType] =
    useState<DropdownOption>();

  useEffect(() => {
    if (step == EditSteps.SLOT_SELECT)
      setHeight(
        `${
          (document.getElementById(`chakra-modal-${TOP_LEVEL_ID}-modal`)
            ?.clientHeight ?? 285) - 285
        }px`,
      );
    if (shouldEditAllAppointments === RecurringAppointmentUpdateOption.ALL) {
      setActiveAppointmentType(null);
    }
  }, [step]);

  const attendingSupports = useMemo(
    () => supportAttendees?.filter((each) => each.isChecked),
    [supportAttendees],
  );
  const queryPeriods = useMemo(
    () =>
      getQueryPeriodsForEditAppointment({
        editAppointment,
        patientAttendee,
        scheduledAppointments,
        supportIds: attendingSupports?.map((each) => each.id),
      }),
    [
      editAppointment,
      patientAttendee,
      scheduledAppointments,
      attendingSupports,
    ],
  );
  const getAvailabilityQuery = useCallback(
    ({
      availabilityRule,
      ignoreAvailabilitySettings,
    }: {
      availabilityRule: AppointmentType;
      ignoreAvailabilitySettings: boolean;
    }) => {
      const availabilityQuery = generateAvailabilityQuery({
        availabilityRule,
        ...(shouldEditAllAppointments ===
          RecurringAppointmentUpdateOption.ONLY && {
          excluded_events: [
            { event_id: editAppointment.eventId },
            ...(cronofyEventUids ?? []).map((each) => ({ event_uid: each })),
          ],
        }),
        hostCalendarInfo: {
          calendarId: hostAttendee?.calendarId,
          sub: hostAttendee?.sub,
        },
        ignoreAvailabilitySettings,
        intervalInMinutes:
          getIntervalInMinutesByAppointmentType(availabilityRule),
        providerCalendarsInfo: getParticipatingProviderCalendarInfo(
          [...providerAttendees, ...externalAttendees],
          hostAttendee,
        ),
        query_periods: queryPeriods,
        shouldAllProvidersAvailable,
      });

      setAvailabilityQuery(availabilityQuery);
      return availabilityQuery;
    },
    [
      shouldEditAllAppointments,
      editAppointment.eventId,
      cronofyEventUids,
      hostAttendee,
      providerAttendees,
      externalAttendees,
      shouldAllProvidersAvailable,
      setAvailabilityQuery,
      queryPeriods,
    ],
  );

  const resetAvailabilityRef = useRef(getAvailabilityQuery);
  useEffect(() => {
    resetAvailabilityRef.current = getAvailabilityQuery;
  }, [getAvailabilityQuery]);

  const handleNotifications = useCallback(async (data: CalendarEvent) => {
    const { body, slot, type } = data?.notification ?? {};
    if (
      type === 'error' &&
      body &&
      Object.entries(body).find(([_k, v]: [string, { key: string }[]]) => {
        return v?.[0]?.key === UNKNOWN_AVAILABILITY_RULE_ERROR;
      })
    ) {
      setHostManagedAvailability({ cronofyRule: null });
      resetAvailabilityRef.current?.({
        availabilityRule: selectedAppointmentType,
        ignoreAvailabilitySettings: true,
      });
      setIgnoreAvailabilitySettings(true);
      setDisableIgnoreAvailabilitySettings(true);
    }

    if (type === 'slot_selected') {
      const selectedDate = toISO(slot.start);
      onAppointmentSlotSelect(selectedDate);
    }
  }, []);

  const fetchAvailability = async (
    elementToken: string,
    appointmentType: AppointmentType,
  ) => {
    const localTimezone = DateTime.now().zoneName;
    const selectedTimezone = options?.tzid ?? localTimezone;

    setElementToken(elementToken);

    const config = {
      ...CalendarResourceMapper[
        CalendarResource.RESCHEDULE_APPOINTMENT_DATE_TIME_PICKER
      ].options.config,
      selected_date: convertToLocalTime(
        editAppointment.startDateTime,
        selectedTimezone,
      ).split('T')[0],
    };

    if (appointmentType && hostAttendee) {
      const props = {
        ...CalendarResourceMapper[
          CalendarResource.RESCHEDULE_APPOINTMENT_DATE_TIME_PICKER
        ].options,
        availability_query: getAvailabilityQuery({
          availabilityRule: appointmentType,
          ignoreAvailabilitySettings,
        }),
        shouldAllProvidersAvailable,
        config,
        callback: handleNotifications,
        element_token: elementToken,
        tzid: selectedTimezone,
      };

      setOptions(props);
    }
  };

  useEffect(() => {
    if (!cronofyEventUids || !selectedAppointmentType) return;
    const { patientAppointments, supportAppointments } = scheduledAppointments;

    if (
      appointmentStartDateTime ||
      ([...patientAppointments, ...supportAppointments].length === 0 &&
        selectedAppointmentType.attendeesAllowed ===
          AppointmentAttendeesAllowed.ALL)
    )
      return;
    const attendeesWithSub = [
      hostAttendee,
      patientAttendee,
      ...providerAttendees,
      ...externalAttendees,
    ].filter((attendee) => attendee?.sub);
    if (attendeesWithSub.length === 0 || !attendees.hostAttendee?.sub) {
      if (attendeesWithSub.some((attendee) => attendee.sub)) {
        console.warn('Host attendee or sub is missing');
      }
      return;
    }
    generateElementToken({
      callback: (token: ElementTokenData) => {
        if (token?.elementToken?.token) {
          fetchAvailability(token.elementToken.token, selectedAppointmentType);
        } else {
          console.warn('Failed to get valid element token:', token);
        }
      },
      subs: [...new Set(attendeesWithSub.map((attendee) => attendee.sub))],
    });
  }, [
    selectedAppointmentType,
    scheduledAppointments,
    cronofyEventUids,
    hostAttendee,
    externalAttendees,
  ]);

  const toggleIgnoreAvailabilitySettings = (): void => {
    if (!selectedAppointmentType || !hostAttendee) return;
    const nextIgnoreAvailabilitySettings = !ignoreAvailabilitySettings;
    getAvailabilityQuery({
      availabilityRule: selectedAppointmentType,
      ignoreAvailabilitySettings: nextIgnoreAvailabilitySettings,
    });
    setIgnoreAvailabilitySettings(nextIgnoreAvailabilitySettings);
    setHostManagedAvailability({
      cronofyRule: ignoreAvailabilitySettings
        ? (selectedAppointmentType.cronofyRule ??
          selectedAppointmentType.appointmentType)
        : null,
      shouldAllProvidersAvailable,
    });
    onAppointmentSlotSelect(null);
  };

  const onTimezoneChange = (tzid: string): void => {
    setOptions((options) => ({ ...options, tzid }));
    onTimeZoneChange?.(tzid);
    onAppointmentSlotSelect(null);
  };

  const onAppointmentTypeChange = (option: DropdownOption) => {
    setActiveAppointmentType(option);
    handleAppointmentTypeChange(option.value);
  };

  const isDataLoading = !hostAttendee && supportAttendees?.length === 0;

  useEffect(() => {
    if (elementToken) {
      fetchAvailability(elementToken, selectedAppointmentType);
    }
  }, [
    selectedAppointmentType,
    providerAttendees,
    hostAttendee,
    patientAttendee,
    supportAttendees,
    shouldEditAllAppointments,
  ]);

  const onParticipantCardClick = (
    participant: Participant,
    userType: ParticipantTypes,
  ) => {
    setIgnoreAvailabilitySettings(false);
    setDisableIgnoreAvailabilitySettings(false);
    handleParticipantSelect(participant.id, userType);
  };

  const getKebabOptions = (participant: Participant) => {
    if (participant?.isEligibleToHost) {
      return [
        {
          id: `make-${participant.id}-host`,
          label: 'Make host',
          leftIcon: FAIconName.userCheckRegular,
          leftIconColor: 'neutral.primary',
          onClick: () => {
            onParticipantCardClick(participant, ParticipantTypes.HOST);
          },
        },
      ];
    }
    return null;
  };

  const recurrenceOptions = (selectedAppointmentType: AppointmentType) => {
    const { appointmentTypeExtId, appointmentType, durationInMinutes } =
      selectedAppointmentType ?? {};
    const option = {
      label: `${appointmentType}, ${durationInMinutes} minutes`,
      value: appointmentTypeExtId,
    };
    return getRecurrenceOptions({
      excludeMonthly: !isMonthlyAllowedAppointmentTypes(option),
    });
  };

  const handleOnParticipantAdded = (participant: GetProviderByIdResponse) => {
    const newParticipant: Participant = {
      calendarId: participant.calendarId,
      id: participant.userId,
      isChecked: true,
      isEligibleToHost: selectedAppointmentType.providerTypeNames.includes(
        participant.providerTypeDetails.name,
      ),
      primaryText: `${participant.firstName} ${participant.lastName}`,
      secondaryText: getCaption({
        timezoneDetails: participant.timeZoneDetails,
        type: participant.providerTypeDetails.name,
      }),
      sub: participant.sub,
      timezone: participant.timeZoneDetails?.id,
      type: ParticipantTypes.PROVIDER,
    };

    const attendeesWithSub = [
      hostAttendee,
      newParticipant,
      patientAttendee,
      ...providerAttendees,
      ...externalAttendees,
    ].filter((attendee) => attendee?.sub);
    generateElementToken({
      callback: (token: ElementTokenData) => {
        if (token?.elementToken?.token) {
          fetchAvailability(token.elementToken.token, selectedAppointmentType);
        } else {
          console.warn('Failed to get valid element token:', token);
        }
      },
      subs: [...new Set(attendeesWithSub.map((attendee) => attendee.sub))],
    });

    if (hostAttendee) {
      onProviderAdded([...externalAttendees, newParticipant]);
    } else if (newParticipant.isEligibleToHost) {
      setHostAttendee(newParticipant);
    } else {
      onProviderAdded([...externalAttendees, newParticipant]);
    }
  };

  const getShouldDisableRecurrence = (): boolean => {
    const { frequency } = editAppointment;
    return (
      frequency !== Recurrence.DOES_NOT_REPEAT &&
      shouldEditAllAppointments === RecurringAppointmentUpdateOption.ONLY
    );
  };

  return (
    <ChakraBox display="block" height="55vh">
      <SkeletonLoader
        id={`${TOP_LEVEL_ID}__Skeleton-loader`}
        isLoaded={!isDataLoading}
      >
        <AvailabilitySettings
          activeAppointmentType={
            activeAppointmentType ?? activeAppointmentTypeMemo
          }
          appointmentTypeOptions={appointmentTypeOptions}
          disableIgnoreAvailabilitySettings={disableIgnoreAvailabilitySettings}
          ignoreAvailabilitySettings={ignoreAvailabilitySettings}
          onAppointmentTypeChange={onAppointmentTypeChange}
          onFrequencyChange={onFrequencyChange}
          onToggleIgnoreAvailability={toggleIgnoreAvailabilitySettings}
          recurrenceOption={recurrenceOption}
          recurrenceOptions={recurrenceOptions(selectedAppointmentType)}
          shouldDisableRecurrence={getShouldDisableRecurrence()}
          shouldEditAllAppointments={
            shouldEditAllAppointments === RecurringAppointmentUpdateOption.ALL
          }
          showAppointmentType
          showRecurrence
          topLevelId={TOP_LEVEL_ID}
        />

        <ChakraFlex
          gap="36px"
          height={height}
          justifyContent="space-between"
          position="relative"
        >
          <ParticipantsList
            getKebabOptions={getKebabOptions}
            handleParticipantAdded={handleOnParticipantAdded}
            height="100%"
            hostAttendee={hostAttendee}
            onParticipantCardClick={onParticipantCardClick}
            patientAttendee={patientAttendee}
            providerAttendees={[...providerAttendees, ...externalAttendees]}
            shouldEditAllAppointments={
              shouldEditAllAppointments === RecurringAppointmentUpdateOption.ALL
            }
            showParticipants
            supportAttendees={supportAttendees}
            topLevelId={TOP_LEVEL_ID}
          />

          <ChakraBox
            height="100%"
            onClick={(e) => e.preventDefault()}
            overflow="auto"
            width="66%"
          >
            <ChakraBox
              position="absolute"
              right="40px"
              top="-6px"
              width="250px"
              zIndex="1"
            >
              <TimezoneMenuV2 onChange={onTimezoneChange} />
            </ChakraBox>
            <ChakraBox
              id={
                CalendarResourceMapper[
                  CalendarResource.RESCHEDULE_APPOINTMENT_DATE_TIME_PICKER
                ].options.target_id
              }
              style={{
                display: !isPendingAvailabilityQuery ? 'block' : 'none',
                height: '100%',
              }}
            />
          </ChakraBox>
        </ChakraFlex>
      </SkeletonLoader>
    </ChakraBox>
  );
};

export default EditAppointmentSlotSelectionStep;
